import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getAccessToken } from '@/lib/tokenStore';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, Zap, Flame, Droplets, Save, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAAND_NAMEN = ['', 'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];
const MAAND_KORT = ['', 'jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

const hdrs = () => {
  const t = getAccessToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmt = (val) => val != null ? Number(val).toLocaleString('nl-NL') : '—';

// ── Year Overview Table ─────────────────────────────────────────────────────
const JaaroverzichtTabel = ({ registraties, jaar, onMaandClick, bronwaterAanwezig }) => {
  const now = new Date();
  const huidigeMaand = now.getFullYear() === jaar ? now.getMonth() + 1 : null;

  const regMap = {};
  (registraties || []).forEach(r => { regMap[r.maand] = r; });

  const BronIcon = ({ bron }) => (
    <span className="ml-1 text-[10px] opacity-60" title={bron === 'meterstand_checklist' ? 'Via meterstandchecklist' : 'Handmatig'}>
      {bron === 'meterstand_checklist' ? '\u{1F4CB}' : '\u{270F}\u{FE0F}'}
    </span>
  );

  return (
    <div className="overflow-x-auto" data-testid="jaaroverzicht-tabel">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left bg-slate-50">
            <th className="py-2.5 px-3 font-semibold text-slate-600 whitespace-nowrap">Maand</th>
            <th className="py-2.5 px-3 font-semibold text-amber-700 whitespace-nowrap"><Zap className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Stroom stand</th>
            <th className="py-2.5 px-3 font-semibold text-amber-700 whitespace-nowrap">Verbruik</th>
            <th className="py-2.5 px-3 font-semibold text-orange-700 whitespace-nowrap"><Flame className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Gas stand</th>
            <th className="py-2.5 px-3 font-semibold text-orange-700 whitespace-nowrap">Verbruik</th>
            <th className="py-2.5 px-3 font-semibold text-blue-700 whitespace-nowrap"><Droplets className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Leidingwater stand</th>
            <th className="py-2.5 px-3 font-semibold text-blue-700 whitespace-nowrap">Verbruik</th>
            {bronwaterAanwezig && (
              <>
                <th className="py-2.5 px-3 font-semibold text-cyan-700 whitespace-nowrap"><Droplets className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Bronwater stand</th>
                <th className="py-2.5 px-3 font-semibold text-cyan-700 whitespace-nowrap">Verbruik</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => {
            const r = regMap[m];
            const isCurrent = m === huidigeMaand;
            const hasData = !!r;
            const bron = r?.bron || 'handmatig';

            return (
              <tr
                key={m}
                onClick={() => onMaandClick(m)}
                className={`cursor-pointer transition-colors border-b border-slate-50 ${
                  isCurrent ? 'bg-[#ffc315]/5' : hasData ? 'hover:bg-slate-50' : 'text-slate-300 hover:bg-slate-50/50'
                }`}
                style={isCurrent ? { borderLeft: '3px solid #ffc315' } : { borderLeft: '3px solid transparent' }}
                data-testid={`jaar-rij-${m}`}
              >
                <td className={`py-2 px-3 font-medium ${isCurrent ? 'text-[#1f2548]' : hasData ? 'text-slate-700' : 'text-slate-400'}`}>
                  {MAAND_NAMEN[m]}
                  {isCurrent && <span className="ml-1.5 text-[10px] text-[#ffc315] font-bold uppercase">nu</span>}
                </td>
                <td className="py-2 px-3">{hasData ? <>{fmt(r.stroom?.meterstand)}<BronIcon bron={bron} /></> : '—'}</td>
                <td className="py-2 px-3 font-medium">{hasData ? `${fmt(r.stroom?.verbruik)} kWh` : '—'}</td>
                <td className="py-2 px-3">{hasData ? <>{fmt(r.gas?.meterstand)}<BronIcon bron={bron} /></> : '—'}</td>
                <td className="py-2 px-3 font-medium">{hasData ? <>{fmt(r.gas?.verbruik)} m&sup3;</> : '—'}</td>
                <td className="py-2 px-3">{hasData ? <>{fmt(r.water?.meterstand)}<BronIcon bron={bron} /></> : '—'}</td>
                <td className="py-2 px-3 font-medium">{hasData ? <>{fmt(r.water?.verbruik)} m&sup3;</> : '—'}</td>
                {bronwaterAanwezig && (
                  <>
                    <td className="py-2 px-3">{hasData && r.bronwater?.meterstand != null ? <>{fmt(r.bronwater.meterstand)}<BronIcon bron={bron} /></> : '—'}</td>
                    <td className="py-2 px-3 font-medium">{hasData && r.bronwater?.verbruik != null ? <>{fmt(r.bronwater.verbruik)} m&sup3;</> : '—'}</td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Inline Form Row ─────────────────────────────────────────────────────────
const NutInput = ({ icon: Icon, iconColor, label, eenheid, meterstand, onChange, verbruik, vorigeMeterstand, bron }) => (
  <div className="space-y-2" data-testid={`${label.toLowerCase()}-section`}>
    <div className="flex items-center gap-2" style={{ color: iconColor }}>
      <Icon className="w-4 h-4" />
      <span className="font-semibold text-slate-800 text-sm">{label}</span>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
      <div>
        <Label className="text-xs">Meterstand ({eenheid})</Label>
        <Input type="number" min="0" placeholder="bv. 45230" value={meterstand} onChange={e => onChange(e.target.value)}
          data-testid={`${label.toLowerCase()}-meterstand-input`} className="h-9" />
        {vorigeMeterstand !== null && <p className="text-[10px] text-slate-400 mt-0.5">Vorige: {Number(vorigeMeterstand).toLocaleString('nl-NL')} ({bron === 'beginstand' ? 'beginstand' : 'vorige'})</p>}
      </div>
      <div>
        <Label className="text-xs">Berekend verbruik</Label>
        <div className="h-9 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 text-slate-700 text-sm font-medium"
          data-testid={`${label.toLowerCase()}-verbruik-display`}>
          {verbruik !== null ? `${Number(verbruik).toLocaleString('nl-NL')} ${eenheid}` : '—'}
        </div>
      </div>
    </div>
  </div>
);

// ── Verbruik Form ───────────────────────────────────────────────────────────
const VerbruikForm = ({ locatieId, jaar, maand, onSaved, bronwaterAanwezig }) => {
  const [form, setForm] = useState({ stroomMeterstand: '', gasMeterstand: '', waterMeterstand: '', bronwaterMeterstand: '', notities: '' });
  const [vorige, setVorige] = useState({ stroom: null, gas: null, water: null, bronwater: null });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);

  useEffect(() => {
    if (!locatieId || !jaar || !maand) return;
    setLoading(true);
    Promise.all([
      fetch(`${API}/locaties/${locatieId}/verbruik/${jaar}/${maand}`, { headers: hdrs() }),
      fetch(`${API}/locaties/${locatieId}/verbruik/vorige-meterstand?jaar=${jaar}&maand=${maand}`, { headers: hdrs() })
    ]).then(async ([regRes, prevRes]) => {
      if (regRes.ok) {
        const d = await regRes.json();
        setForm({ stroomMeterstand: d.stroom?.meterstand ?? '', gasMeterstand: d.gas?.meterstand ?? '', waterMeterstand: d.water?.meterstand ?? '', bronwaterMeterstand: d.bronwater?.meterstand ?? '', notities: d.notities || '' });
        setIsUpdate(true);
      } else {
        setForm({ stroomMeterstand: '', gasMeterstand: '', waterMeterstand: '', bronwaterMeterstand: '', notities: '' });
        setIsUpdate(false);
      }
      if (prevRes.ok) setVorige(await prevRes.json());
    }).catch(() => {
      setForm({ stroomMeterstand: '', gasMeterstand: '', waterMeterstand: '', bronwaterMeterstand: '', notities: '' });
      setIsUpdate(false);
    }).finally(() => setLoading(false));
  }, [locatieId, jaar, maand]);

  const calc = (ms, key) => {
    if (!ms || ms === '') return null;
    const c = parseInt(ms, 10);
    if (isNaN(c)) return null;
    return Math.max(0, c - (vorige[key]?.meterstand ?? 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.stroomMeterstand || !form.gasMeterstand || !form.waterMeterstand) { toast.error('Vul alle meterstanden in'); return; }
    if (bronwaterAanwezig && !form.bronwaterMeterstand) { toast.error('Vul ook de bronwater-meterstand in'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/locaties/${locatieId}/verbruik`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...hdrs() },
        body: JSON.stringify({ jaar, maand, stroom: { meterstand: Number(form.stroomMeterstand) }, gas: { meterstand: Number(form.gasMeterstand) }, water: { meterstand: Number(form.waterMeterstand) }, notities: form.notities, ...(bronwaterAanwezig ? { bronwater: { meterstand: Number(form.bronwaterMeterstand) } } : {}) })
      });
      if (res.ok) { const r = await res.json(); toast.success(r.message || 'Opgeslagen'); setIsUpdate(true); onSaved?.(); }
      else { const err = await res.json(); toast.error(err.detail || 'Opslaan mislukt'); }
    } catch { toast.error('Opslaan mislukt'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="verbruik-form">
      <NutInput icon={Zap} iconColor="#d97706" label="Stroom" eenheid="kWh" meterstand={form.stroomMeterstand}
        onChange={v => setForm(p => ({ ...p, stroomMeterstand: v }))} verbruik={calc(form.stroomMeterstand, 'stroom')}
        vorigeMeterstand={vorige.stroom?.meterstand ?? null} bron={vorige.stroom?.bron} />
      <NutInput icon={Flame} iconColor="#ea580c" label="Gas" eenheid="m³" meterstand={form.gasMeterstand}
        onChange={v => setForm(p => ({ ...p, gasMeterstand: v }))} verbruik={calc(form.gasMeterstand, 'gas')}
        vorigeMeterstand={vorige.gas?.meterstand ?? null} bron={vorige.gas?.bron} />
      <NutInput icon={Droplets} iconColor="#2563eb" label="Leidingwater" eenheid="m³" meterstand={form.waterMeterstand}
        onChange={v => setForm(p => ({ ...p, waterMeterstand: v }))} verbruik={calc(form.waterMeterstand, 'water')}
        vorigeMeterstand={vorige.water?.meterstand ?? null} bron={vorige.water?.bron} />
      {bronwaterAanwezig && (
        <NutInput icon={Droplets} iconColor="#0891b2" label="Bronwater" eenheid="m³" meterstand={form.bronwaterMeterstand}
          onChange={v => setForm(p => ({ ...p, bronwaterMeterstand: v }))} verbruik={calc(form.bronwaterMeterstand, 'bronwater')}
          vorigeMeterstand={vorige.bronwater?.meterstand ?? null} bron={vorige.bronwater?.bron} />
      )}
      <div>
        <Label className="text-xs">Notities</Label>
        <Textarea placeholder="Eventuele opmerkingen..." value={form.notities}
          onChange={e => setForm(p => ({ ...p, notities: e.target.value }))} rows={2} data-testid="notities-input" />
      </div>
      <Button type="submit" disabled={saving} className="bg-[#1f2548] hover:bg-[#2a3060]" data-testid="verbruik-submit-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
        {isUpdate ? 'Bijwerken' : 'Opslaan'}
      </Button>
    </form>
  );
};

// ── Main Verbruik Page ──────────────────────────────────────────────────────
const Verbruik = () => {
  const { selectedLocatie, selectedLocatieData } = useOutletContext();
  const now = new Date();
  const [jaar, setJaar] = useState(now.getFullYear());
  const [maand, setMaand] = useState(now.getMonth() + 1);
  const [registraties, setRegistraties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bronwaterAanwezig, setBronwaterAanwezig] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    if (!selectedLocatie) { setBronwaterAanwezig(false); return; }
    fetch(`${API}/locaties/${selectedLocatie}`, { headers: hdrs() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setBronwaterAanwezig(!!d?.nutsvoorzieningen?.bronwater?.aanwezig))
      .catch(() => setBronwaterAanwezig(false));
  }, [selectedLocatie]);

  const fetchData = useCallback(async () => {
    if (!selectedLocatie) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/locaties/${selectedLocatie}/verbruik?jaar=${jaar}`, { headers: hdrs() });
      if (res.ok) setRegistraties(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [selectedLocatie, jaar]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleMaandClick = (m) => {
    setMaand(m);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  if (loading) return <div className="flex items-center justify-center h-64" data-testid="verbruik-loading"><Loader2 className="w-8 h-8 animate-spin text-[#1f2548]" /></div>;

  return (
    <div className="space-y-6 max-w-5xl mx-auto px-4 sm:px-0" data-testid="verbruik-page">
      {/* Header + Year selector */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1f2548]" data-testid="verbruik-title">Verbruik — {selectedLocatieData?.naam}</h1>
          <p className="text-slate-500 text-sm">Jaaroverzicht meterstanden en verbruik</p>
        </div>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-1 py-1" data-testid="jaar-selector">
          <button onClick={() => setJaar(j => j - 1)} className="p-1.5 hover:bg-slate-100 rounded" data-testid="jaar-prev"><ChevronLeft className="w-4 h-4" /></button>
          <span className="px-3 font-semibold text-[#1f2548] text-sm tabular-nums">{jaar}</span>
          <button onClick={() => setJaar(j => j + 1)} className="p-1.5 hover:bg-slate-100 rounded" disabled={jaar >= now.getFullYear()} data-testid="jaar-next"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Section A: Jaaroverzicht */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
          <h2 className="font-semibold text-slate-700 text-sm">Jaaroverzicht {jaar}</h2>
          <p className="text-xs text-slate-400">Klik op een maand om meterstanden in te vullen</p>
        </div>
        <JaaroverzichtTabel registraties={registraties} jaar={jaar} onMaandClick={handleMaandClick} bronwaterAanwezig={bronwaterAanwezig} />
      </div>

      {/* Section B: Invulformulier */}
      <div ref={formRef} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="invulformulier-section">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
          <h2 className="font-semibold text-[#1f2548]">Registratie — {MAAND_NAMEN[maand]} {jaar}</h2>
        </div>
        <div className="p-5">
          <VerbruikForm locatieId={selectedLocatie} jaar={jaar} maand={maand} onSaved={fetchData} bronwaterAanwezig={bronwaterAanwezig} />
        </div>
      </div>
    </div>
  );
};

export default Verbruik;
